import styled, { css } from 'styled-components';

import Image from 'components/Image';
import RichText from 'components/RichText';
import Roundel from 'components/Roundel';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { Grid, columns } from 'styles/grid';
import TYPO, { H5 } from 'styles/typography';

const Container = styled(Grid)`
  padding: 0 var(--gutter-side) 60px;
  color: ${COLORS.forest};

  &:last-child {
    padding-bottom: 0;
  }

  ${BREAKPOINTS.small`
    padding-bottom: 100px;
    display: flex;
  `}

  ${BREAKPOINTS.medium`
    padding: 0 0 100px;
    display: flex;
  `}
`;

const CardImage = styled.div`
  ${columns(null, 12)}
  margin-bottom: 20px;
  position: relative;

  ${BREAKPOINTS.small`
    margin-bottom: 0;
    ${({ isReverse }) => (isReverse ? columns(7, 6) : columns(null, 6))};
  `}

  ${BREAKPOINTS.medium`
    ${({ isReverse }) =>
      isReverse
        ? css`
            ${columns(7, 5)}
            margin: 0 0 0 40px;
          `
        : css`
            ${columns(2, 5)}
            margin: 0 40px 0 0;
          `};
  `}
`;

const ImageMobile = styled(Image)`
  ${BREAKPOINTS.small`
    display: none;
  `}
`;

const ImageDesktop = styled(Image)`
  display: none;

  ${BREAKPOINTS.small`
    display: block;
    height: 100%;
    min-height: 320px;
  `}
`;

const RoundelStyled = styled(Roundel)`
  position: absolute;
  top: 0;
  right: 25px;
  transform: translateY(-50%) rotate(-10deg);

  ${BREAKPOINTS.small`
    right: -40px;
    top: 20px;

    ${({ isReverse }) =>
      isReverse
        ? css`
            left: -40px;
            transform: rotate(10deg);
          `
        : css`
            transform: rotate(-10deg);
          `};
  `}
`;

const Content = styled.div`
  --padding: 30px;
  ${columns(null, 12)}

  ${BREAKPOINTS.small`
    max-width: 460px;
    display: flex;
    flex-direction: column;

    ${({ isReverse }) =>
      isReverse
        ? css`
            ${columns(null, 6)}
            padding: var(--padding) 40px var(--padding) 0;
            justify-self: end;
          `
        : css`
            ${columns(7, 6)}
            padding: var(--padding) 0 var(--padding) 40px;
            justify-self: start;
          `};
  `}

  ${BREAKPOINTS.medium`
    --padding: 40px;
    ${({ isReverse }) => (isReverse ? columns(2, 5) : columns(7, 5))};
  `}

  ${BREAKPOINTS.large`
    --padding: 60px;
  `}
`;

const Title = styled(H5)`
  margin-bottom: 14px;

  ${BREAKPOINTS.small`
    margin-bottom: 10px;
  `}
`;

const RichTextStyled = styled(RichText)`
  *:not(li) {
    ${TYPO.p1}
    color: ${COLORS.forest.opacity(0.8)};
    opacity: 1;
  }

  *:first-child {
    margin-top: 0;
  }

  *:last-child {
    margin-bottom: 0;
  }

  ul:not(ul:first-child) {
    margin-top: 40px;
  }

  li {
    ${TYPO.p3}
    margin-bottom: 23px;
    display: flex;

    &:before {
      content: '';
      margin-right: 10px;
      display: block;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      flex-shrink: 0;
      background: url(/images/icons/tick-pink.svg) no-repeat center;
      background-color: ${({ isReverse }) =>
        isReverse ? COLORS.purpleHot : COLORS.orange};
    }
  }

  ${BREAKPOINTS.small`
    li {
      margin-bottom: 20px;
    }
  `}
`;

const Card = ({ title, image, textBlock, idx, isReverse }) => {
  const itemNo = idx >= 10 ? idx : `0${idx}`;
  return (
    <Container>
      <CardImage isReverse={isReverse}>
        <ImageMobile image={image} />
        <ImageDesktop image={image} layout="fill" />
        <RoundelStyled isReverse={isReverse}>{itemNo}</RoundelStyled>
      </CardImage>

      <Content isReverse={isReverse}>
        <Title as="h3" small weight="bold">
          {title}
        </Title>
        <RichTextStyled
          html={textBlock?.html}
          isReverse={isReverse}
          textIndent
        />
      </Content>
    </Container>
  );
};

export default Card;
